import { useState, useEffect, useRef, useCallback } from 'react';

interface QuoteSegment {
  text: string;
  highlighted: boolean;
}

interface Testimonial {
  company: string;
  industry: string;
  quote: QuoteSegment[];
  name: string;
  role: string;
  stat: string;
  statLabel: string;
}

const testimonials: Testimonial[] = [
  {
    company: 'Viyar',
    industry: 'Виробництво меблів',
    quote: [
      { text: 'Для нас майже неможливо працювати без системи автоматизації. ', highlighted: false },
      { text: 'Наразі в базі понад 10 тис. резюме.', highlighted: true },
    ],
    name: 'Тетяна Режевська',
    role: 'керівниця відділу з підбору персоналу',
    stat: '10 000+',
    statLabel: 'резюме в базі',
  },
  {
    company: 'Міжнародна логістична компанія',
    industry: 'Логістика',
    quote: [
      { text: 'Після впровадження HURMA ', highlighted: false },
      { text: 'ми нарешті почали проводити співбесіди, а не тести.', highlighted: true },
      { text: ' Наймаючі менеджери перестали скаржитися на якість кандидатів. Два місяці — і я не уявляю, як ми працювали без цього раніше.', highlighted: false },
    ],
    name: 'Head of HR',
    role: 'керівниця відділу підбору',
    stat: '2 місяці',
    statLabel: 'до повної трансформації',
  },
  {
    company: 'TechVill',
    industry: 'Технології',
    quote: [
      { text: 'Раніше закривали 4–5 вакансій на місяць. Зараз з тією ж кількістю рекрутерів та HURMA — ', highlighted: false },
      { text: '19.', highlighted: true },
    ],
    name: 'Світлана Онищенко',
    role: 'CPO',
    stat: '4.7×',
    statLabel: 'зростання закриття вакансій',
  },
  {
    company: 'Всеукраїнська роздрібна мережа',
    industry: 'Ритейл',
    quote: [
      { text: 'Ми більше не боїмося одночасного відкриття 10 нових точок: ', highlighted: false },
      { text: 'система вибере кращих за лічені хвилини.', highlighted: true },
      { text: ' HURMA змінила те, як ми взагалі думаємо про найм.', highlighted: false },
    ],
    name: 'Керівниця відділу підбору персоналу',
    role: 'керівниця напряму',
    stat: '10 точок',
    statLabel: 'одночасно без ризику',
  },
];

const AUTO_PLAY_INTERVAL = 5000;

export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback((index: number) => {
    setActive(index);
  }, []);

  const prev = useCallback(() => {
    setActive((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);

  const next = useCallback(() => {
    setActive((prev) => (prev + 1) % testimonials.length);
  }, []);

  // Auto-play
  useEffect(() => {
    if (isPaused) return;
    timerRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials.length);
    }, AUTO_PLAY_INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused]);

  // Reset timer on manual interaction
  const handleManual = useCallback((fn: () => void) => {
    setIsPaused(true);
    fn();
    // Resume after a longer pause
    setTimeout(() => setIsPaused(false), AUTO_PLAY_INTERVAL);
  }, []);

  const t = testimonials[active];

  return (
    <section id="testimonials" className="py-24 lg:py-32" style={{ backgroundColor: '#0c1527' }}>
      <div
        className="max-w-[900px] mx-auto px-6 lg:px-10 text-center"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >

        <h2 className="font-heading font-bold text-white leading-[1.08] tracking-tight mb-16 lg:mb-20" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)' }}>
          HURMA працює там,<br />де щодня потрібні люди
        </h2>

        {/* Dynamic content wrapper — fixed height to prevent layout jumps */}
        <div className="min-h-[20rem] flex flex-col items-center justify-center mb-12">

          {/* Big stat */}
          <div className="mb-8">
            <span className="font-heading font-bold text-4xl lg:text-5xl" style={{ color: '#F96B25' }}>{t.stat}</span>
            <span className="text-white/30 text-base ml-3">{t.statLabel}</span>
          </div>

          {/* Quote with highlights */}
          <p className="text-white/70 text-xl lg:text-2xl leading-relaxed mb-8 max-w-[640px] mx-auto">
            «
            {t.quote.map((seg, i) =>
              seg.highlighted ? (
                <strong key={i} className="font-semibold" style={{ color: '#F96B25' }}>{seg.text}</strong>
              ) : (
                <span key={i}>{seg.text}</span>
              )
            )}
            »
          </p>

          {/* Author */}
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(249,107,37,0.12)' }}>
              <i className="ri-user-line text-sm" style={{ color: '#F96B25' }}></i>
            </div>
            <div className="text-left">
              <div className="font-heading font-semibold text-white text-base">{t.name}</div>
              <div className="text-white/35 text-sm">{t.role}</div>
            </div>
          </div>

        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-6">
          <button
            onClick={() => handleManual(prev)}
            className="w-10 h-10 rounded-full border border-white/[0.1] hover:border-white/[0.25] flex items-center justify-center text-white/40 hover:text-white/70 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-sm"></i>
          </button>

          <div className="flex items-center gap-2">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => handleManual(() => goTo(i))}
                className="rounded-full transition-all cursor-pointer"
                style={{
                  width: i === active ? '24px' : '8px',
                  height: '8px',
                  backgroundColor: i === active ? '#F96B25' : 'rgba(255,255,255,0.15)',
                }}
              />
            ))}
          </div>

          <button
            onClick={() => handleManual(next)}
            className="w-10 h-10 rounded-full border border-white/[0.1] hover:border-white/[0.25] flex items-center justify-center text-white/40 hover:text-white/70 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-line text-sm"></i>
          </button>
        </div>

      </div>
    </section>
  );
}